import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { AIStepLayout } from '../masterclass/ai/AIStepLayout';
import { useProgramCreation } from '../ProgramCreationContext';
import { API } from '../../../../api';
import DosenInput from '../../../../pages/new-designs/components/DosenInput';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { useMemberWord } from '../../../../utils/memberTypeLabels';

const MENTORSHIP_STEPS = ['Program name', 'Learning objectives', 'People'];

export function MentorshipObjectives() {
  const navigate = useNavigate();
  const { orgId: orgIdParam = '' } = useProgramContext();
  const { state, dispatch } = useProgramCreation();
  const w = useMemberWord();
  const [objectives, setObjectives] = useState<string[]>(
    state.mentorship.learningObjectives && state.mentorship.learningObjectives.length > 0
      ? state.mentorship.learningObjectives
      : ['']
  );
  const [maxMatchesPerEmployee, setMaxMatchesPerEmployee] = useState<number>(
    state.mentorship.matchSettings?.maxMatchesPerMentee || 1
  );
  const [maxMatchesPerMentor, setMaxMatchesPerMentor] = useState<number>(
    state.mentorship.matchSettings?.maxMatchesPerMentor || 1
  );
  const [sessionsPerMatch, setSessionsPerMatch] = useState<number>(
    state.mentorship.matchSettings?.sessionsPerMatch || 1
  );
  const [saving, setSaving] = useState(false);

  const trimmedObjectives = useMemo(() => objectives.map((o) => o.trim()), [objectives]);
  const allObjectivesFilled = trimmedObjectives.every((obj) => obj.length > 0);
  const hasAtLeastOneObjective = trimmedObjectives.some((obj) => obj.length > 0);
  const canDelete = objectives.length > 1;

  useEffect(() => {
    if (!state.groupId) {
      navigate('../program-name', { replace: true });
    }
  }, [navigate, state.groupId]);

  const matchValuesValid =
    Number(maxMatchesPerEmployee) > 0 &&
    Number(maxMatchesPerMentor) > 0 &&
    Number(sessionsPerMatch) > 0 &&
    !Number.isNaN(maxMatchesPerEmployee) &&
    !Number.isNaN(maxMatchesPerMentor) &&
    !Number.isNaN(sessionsPerMatch);

  const handleNext = async () => {
    if (!state.groupId) return;
    if (!hasAtLeastOneObjective) {
      toast.error('Add at least one learning objective.');
      return;
    }
    if (!matchValuesValid) {
      toast.error('Enter a number above 0 for each match setting.');
      return;
    }

    setSaving(true);
    try {
      const filteredObjectives = trimmedObjectives.filter(Boolean);

      await API.updateLearningObjectives({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: {
          number: filteredObjectives.length,
          objectives: filteredObjectives
        }
      });

      await API.updateAllMatches({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: {
          maxMatchesPerMentee: Number(maxMatchesPerEmployee),
          maxMatchesPerMentor: Number(maxMatchesPerMentor),
          sessionsPerMatch: Number(sessionsPerMatch),
          matchRequest: false
        }
      });

      await API.updateTopicsAndIndustry({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: { topics: ['Topic Creation'] },
        key: 'topics'
      });
      await API.updateTopicsAndIndustry({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: { industries: ['Industry Creation'] },
        key: 'industries'
      });

      dispatch({
        type: 'UPDATE_MENTORSHIP',
        payload: {
          learningObjectives: filteredObjectives,
          matchSettings: {
            maxMatchesPerMentee: Number(maxMatchesPerEmployee),
            maxMatchesPerMentor: Number(maxMatchesPerMentor),
            sessionsPerMatch: Number(sessionsPerMatch)
          }
        }
      });

      navigate('../members');
    } catch (err) {
      console.error(err);
      toast.error('The learning objectives could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateObjective = (value: string, index: number) => {
    setObjectives((prev) => prev.map((obj, idx) => (idx === index ? value : obj)));
  };

  const addObjective = () => setObjectives((prev) => [...prev, '']);
  const removeObjective = (index: number) => setObjectives((prev) => prev.filter((_, idx) => idx !== index));

  return (
    <AIStepLayout
      stepTitle="Learning objectives"
      steps={MENTORSHIP_STEPS}
      title="Learning objectives"
      onBack={() => navigate(-1)}
      onNext={handleNext}
      nextDisabled={saving || !allObjectivesFilled || !matchValuesValid}
      nextLabel="Save"
    >
      <Typography
        sx={{
          color: 'var(--Text-text-secondary, #B6B6B6)',
          fontSize: 14,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          lineHeight: '19.88px',
          wordWrap: 'break-word'
        }}
      >
        Capture what {w('mentor', { plural: true, lower: true })} and {w('mentee', { plural: true, lower: true })}{' '}
        should achieve with this mentorship. Use short, action-oriented objectives.
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {objectives.map((obj, index) => (
          <Box key={index} display="flex" gap={1} alignItems="center">
            <DosenInput
              fullWidth
              label={`Learning objective ${index + 1}`}
              value={obj}
              onChange={(e) => updateObjective(e.target.value, index)}
            />
            {canDelete && (
              <IconButton onClick={() => removeObjective(index)} aria-label="Remove learning objective">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}

        <Box display="flex">
          <DosenButton iconOnly tone="basic" onClick={addObjective} aria-label="Add learning objective">
            <AddIcon sx={{ fontSize: 14, color: '#FDFDFD' }} />
          </DosenButton>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={2} pt={2}>
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif'
          }}
        >
          Match settings
        </Typography>

        <DosenInput
          label={`Maximum matches per ${w('mentee', { lower: true })}`}
          type="number"
          inputProps={{ min: 1 }}
          value={maxMatchesPerEmployee}
          onChange={(e) => setMaxMatchesPerEmployee(Number(e.target.value))}
        />

        <DosenInput
          label={`Maximum matches per ${w('mentor', { lower: true })}`}
          type="number"
          inputProps={{ min: 1 }}
          value={maxMatchesPerMentor}
          onChange={(e) => setMaxMatchesPerMentor(Number(e.target.value))}
        />

        <DosenInput
          label="Required sessions per match"
          type="number"
          inputProps={{ min: 1 }}
          value={sessionsPerMatch}
          onChange={(e) => setSessionsPerMatch(Number(e.target.value))}
        />
      </Box>
    </AIStepLayout>
  );
}

export default MentorshipObjectives;
